import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '~/store/store';
import { getRelativeGetStore } from '~/store/utils';
import { State } from './models';
import { Offer } from '@sunbit-dev/purchase-common/src/models/Offer';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { WorkflowState } from '~/models/WorkflowState';

const initialState: State = {
  purchaseInfo: undefined,
  phoneNumber: undefined,
  offer: undefined,
  selectedPlanIndex: undefined,
  processingPaymentPageVersionToDisplay: 3,
  workflowState: undefined,
};

export const purchase = createSlice({
  name: 'purchaseInfo',
  initialState,
  reducers: {
    setPurchaseInfo: (state, action: PayloadAction<State['purchaseInfo']>) => {
      state.purchaseInfo = action.payload;
    },
    setPurchaseAmount: (state, action: PayloadAction<{ purchaseAmount: number }>) => {
      state.purchaseInfo.amountRequested = action.payload.purchaseAmount;
    },
    setStatus: (state, action: PayloadAction<Status>) => {
      state.purchaseInfo.status = action.payload;
    },
    setOffer: (state, action: PayloadAction<{ offer: Offer; skipDefaultPlanSelection?: boolean }>) => {
      const { offer, skipDefaultPlanSelection = false } = action.payload || {};

      // Sorting from most payments to the least payments
      offer.paymentPlans = offer.paymentPlans.reverse();

      state.offer = offer;

      const matchedPlanIndex = offer.paymentPlans.findIndex(({ id }) => id === offer.chosenPaymentPlanId);
      const chosenPlanIndex = matchedPlanIndex >= 0 ? matchedPlanIndex : undefined;

      if (chosenPlanIndex !== undefined || (skipDefaultPlanSelection && offer.paymentPlans.length > 1)) {
        state.selectedPlanIndex = chosenPlanIndex;
        return;
      }

      state.selectedPlanIndex = offer.paymentPlans.findIndex(({ default: defaultSelectedPlan }) => defaultSelectedPlan);
    },
    setSelectedPlanIndex: (state, action: PayloadAction<{ index: number }>) => {
      state.selectedPlanIndex = action.payload.index;
    },
    setWantedAmount: (state, action: PayloadAction<{ wantedAmount: number }>) => {
      state.purchaseInfo.wantedAmount = action.payload.wantedAmount;
    },
    setReferer: (state, action: PayloadAction<{ referer: string }>) => {
      state.referer = action.payload.referer;
    },
    setProcessingPaymentPageVersionToDisplay: (state, action: PayloadAction<{ version: 1 | 2 | 3 }>) => {
      state.processingPaymentPageVersionToDisplay = action.payload.version;
    },
    setWorkflowState: (state, action: PayloadAction<WorkflowState | undefined>) => {
      state.workflowState = action.payload;
    },
  },
});

export const getStore = getRelativeGetStore<Partial<State>>((store: RootState) => store.purchase);

export const {
  setPurchaseInfo,
  setOffer,
  setSelectedPlanIndex,
  setStatus,
  setWantedAmount,
  setPurchaseAmount,
  setReferer,
  setProcessingPaymentPageVersionToDisplay,
  setWorkflowState,
} = purchase.actions;

export default purchase.reducer;
